import React from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useLocale } from '../contexts/LocaleContext';
import PageHeader from '../components/PageHeader';
import MapView from '../components/MapView';
import DirectionsList from '../components/DirectionsList';
import SocGraph from '../components/SocGraph';
import AccuracyBadge from '../components/AccuracyBadge';
import EngineDiagnostics from '../components/EngineDiagnostics';
import { useTripPlan } from '../hooks/useTripPlan';
import { formatDistance, formatTime, formatEnergy, formatCurrency, formatTemperature, formatPower } from '../lib/locale';

export default function ResultPage() {
  const { tripId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { locale } = useLocale();
  
  const { loading, error, vehicle, route, socTrace, chargingStops, summary } = useTripPlan(searchParams);
  
  if (loading) {
    return (
      <div className="page">
        <PageHeader title="Computing trip" backTo="/" />
        <div className="loading-state">
          Geocoding addresses, computing route, fetching weather, running physics…
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="page">
        <PageHeader title="Trip failed" backTo="/" />
        <div className="error-banner">{error}</div>
        <button className="btn-secondary" onClick={() => navigate('/')}>
          Try again
        </button>
      </div>
    );
  }
  
  if (!summary) return null;
  
  const arrivalRangeKm = vehicle?.battery_kwh && vehicle?.consumption_wh_km
    ? (summary.final_soc_pct / 100) * vehicle.battery_kwh * 1000 / vehicle.consumption_wh_km * 0.85
    : null;
  
  return (
    <div className="page">
      <PageHeader 
        title={`${summary.origin?.name || 'Origin'} → ${summary.destination?.name || 'Destination'}`}
        backTo="/"
      />
      
      <div style={{
        background: 'linear-gradient(180deg, rgba(32, 217, 166, 0.06) 0%, transparent 100%)',
        borderRadius: 14,
        padding: '20px 16px 16px',
        textAlign: 'center',
        marginBottom: 16,
        borderTop: '0.5px solid rgba(32, 217, 166, 0.15)',
      }}>
        <div style={{
          fontSize: 10,
          color: 'var(--accent-primary)',
          textTransform: 'uppercase',
          letterSpacing: '0.1em',
          fontWeight: 500,
          marginBottom: 4,
        }}>
          Arrive with
        </div>
        <div style={{
          fontSize: 48,
          fontWeight: 300,
          color: 'var(--text-primary)',
          lineHeight: 1,
          letterSpacing: '-0.02em',
        }}>
          {Math.round(summary.final_soc_pct)}
          <span style={{ fontSize: 18, color: 'var(--text-tertiary)', marginLeft: 4 }}>
            % ± {summary.confidence_pts}
          </span>
        </div>
        {arrivalRangeKm && (
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 6 }}>
            ~ {formatDistance(arrivalRangeKm, locale)} remaining at destination
          </div>
        )}
      </div>
      
      <div className="stat-grid">
        <div className="stat">
          <p className="stat-lbl">Distance</p>
          <p className="stat-val">{formatDistance(summary.distance_km, locale)}</p>
        </div>
        <div className="stat">
          <p className="stat-lbl">Drive time</p>
          <p className="stat-val">{formatTime(summary.drive_time_min)}</p>
        </div>
        <div className="stat">
          <p className="stat-lbl">{chargingStops ? 'Charging' : 'No stops'}</p>
          <p className="stat-val">
            {chargingStops ? formatTime(summary.charge_time_min) : '—'}
          </p>
        </div>
        <div className="stat">
          <p className="stat-lbl">Energy</p>
          <p className="stat-val">{formatEnergy(summary.total_energy_kwh, { precision: 0 })}</p>
        </div>
      </div>
      
      {vehicle && (
        <AccuracyBadge 
          vehicle={vehicle}
          locale={locale}
          onShopAccessories={() => navigate(`/accessories?vehicle=${vehicle.vehicle_id}`)}
        />
      )}
      
      {route && summary.origin && summary.destination && (
        <>
          <p className="label">Route</p>
          <MapView 
            origin={summary.origin}
            destination={summary.destination}
            route={route}
            stops={chargingStops?.stops || []}
          />
          <DirectionsList maneuvers={route.maneuvers} locale={locale} defaultExpanded={true} />
        </>
      )}
      
      {socTrace && (
        <>
          <p className="label">State of charge along route</p>
          <SocGraph trace={socTrace} locale={locale} />
          <EngineDiagnostics socTrace={socTrace} />
        </>
      )}
      
      {chargingStops?.stops?.length > 0 && (
        <>
          <p className="label">Charging stops ({chargingStops.stops.length})</p>
          {chargingStops.stops.map((stop, i) => (
            <button
              key={stop.station_id || i}
              onClick={() => navigate(`/stop/${stop.station_id}?trip=${tripId}`)}
              style={{
                width: '100%',
                background: 'var(--bg-card)',
                border: '0.5px solid var(--border-subtle)',
                borderRadius: 12,
                padding: 14,
                marginBottom: 8,
                cursor: 'pointer',
                textAlign: 'left',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-primary)' }}>
                    {stop.name || 'Charging stop'}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 2 }}>
                    {stop.address || stop.network} · {formatDistance(stop.distance_from_origin_km, locale)} from start
                  </div>
                </div>
                <div style={{
                  background: 'rgba(255, 184, 77, 0.15)',
                  color: 'var(--accent-warning)',
                  padding: '3px 9px',
                  borderRadius: 6,
                  fontSize: 11,
                  fontWeight: 500,
                }}>
                  {formatPower(stop.peak_dc_kw)}
                </div>
              </div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                paddingTop: 8,
                borderTop: '0.5px solid var(--border-subtle)',
                fontSize: 11,
              }}>
                <span style={{ color: 'var(--text-tertiary)' }}>
                  Arrive <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{Math.round(stop.arrive_soc_pct)}%</span> 
                  {' → '}
                  Depart <span style={{ color: 'var(--accent-primary)', fontWeight: 500 }}>{Math.round(stop.depart_soc_pct)}%</span>
                </span>
                <span style={{ color: 'var(--accent-warning)', fontWeight: 500 }}>
                  {formatTime(stop.charge_time_min, { compact: true })}
                </span>
              </div>
              {stop.cost_local > 0 && (
                <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 6 }}>
                  Est. cost: {formatCurrency(stop.cost_local, locale)}
                </div>
              )}
            </button>
          ))}
        </>
      )}
      
      {!chargingStops && (
        <div style={{
          background: 'rgba(32, 217, 166, 0.06)',
          border: '0.5px solid rgba(32, 217, 166, 0.2)',
          borderRadius: 12,
          padding: 14,
          textAlign: 'center',
          marginTop: 12,
        }}>
          <div style={{ fontSize: 13, color: 'var(--accent-primary)', fontWeight: 500 }}>
            Direct route — no charging needed
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 4 }}>
            Your vehicle has comfortable range for this trip
          </div>
        </div>
      )}
      
      <button
        className="btn-primary"
        onClick={() => navigate(`/drive/${tripId}?${searchParams.toString()}`)}
      >
        Start drive mode
      </button>
    </div>
  );
}
